import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Smartphone, XCircle } from "lucide-react";
import api, { PLATFORM_NAME, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const Row = ({ label, value }) =>
  value ? (
    <div className="border-b border-ink-600 py-3">
      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className="mt-1 text-sm font-medium text-slate-100">{value}</p>
    </div>
  ) : null;

export default function DriverTrip() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = () =>
    api.get(`/public/trip/${token}`).then(({ data }) => setData(data)).catch((e) => setError(errMsg(e)));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (error) return <div className="flex min-h-screen items-center justify-center bg-ink-900 p-6"><p data-testid="driver-trip-error" className="text-sm text-red-400">{error}</p></div>;
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-ink-900"><Loader2 className="h-5 w-5 animate-spin text-gold" /></div>;

  const { manifest: m, company: c } = data;
  const answered = !["draft", "sent"].includes(m.status);
  const done = ["completed", "paid"].includes(m.status);

  return (
    <div data-testid="driver-trip-page" className="min-h-screen bg-ink-900 px-4 py-10 text-slate-200">
      <div className="mx-auto max-w-xl rise">
        <div className="rounded-t-xl bg-ink-800 p-6" style={{ borderTop: `3px solid ${c.branding?.primary_color || "#D4AF37"}` }}>
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">{c.name}</p>
          <h1 className="mt-2 text-2xl font-bold text-slate-50">Trip Manifest #{m.number}</h1>
          <p className="mt-1 text-xs text-slate-500">{c.phone}{c.email ? ` · ${c.email}` : ""}</p>
        </div>

        <div className="rounded-b-xl border border-t-0 border-ink-600 bg-ink-800/60 p-6">
          <Row label="Passenger" value={m.customer_name} />
          <Row label="Passenger phone" value={m.customer_phone} />
          <Row label="Pickup" value={m.pickup?.address} />
          <Row label="Drop-off" value={m.dropoff?.address} />
          <Row label="Flight" value={[m.airline, m.flight_number, m.terminal && `Terminal ${m.terminal}`].filter(Boolean).join(" · ")} />
          <Row label="Date & pickup time" value={[m.trip_date, m.pickup_time].filter(Boolean).join(" · ")} />
          <Row label="Vehicle" value={m.vehicle_name} />
          {m.driver_payout && (
            <div className="border-b border-ink-600 py-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Your payout</p>
              <p data-testid="driver-trip-payout" className="mt-1 font-display text-xl text-emerald-300">
                ${Number(m.driver_payout.amount).toFixed(2)}
              </p>
              <p className="font-mono text-[10px] text-slate-500">{m.driver_payout.label}</p>
            </div>
          )}
          <Row label="Special instructions" value={m.special_instructions} />

          {answered ? (
            <div data-testid="driver-response-state" className="mt-6 flex items-center gap-3 rounded-lg border border-ink-600 bg-ink-900/60 p-4">
              {m.status === "declined" ? <XCircle className="h-5 w-5 text-red-400" /> : <CheckCircle2 className="h-5 w-5 text-emerald-400" />}
              <p className="text-sm text-slate-200">
                {done
                  ? "This job is complete."
                  : m.status === "declined"
                    ? "You declined this job."
                    : `You have this job${m.responded_at ? ` · accepted ${new Date(m.responded_at).toLocaleString()}` : ""}.`}
              </p>
            </div>
          ) : (
            <div data-testid="driver-open-app" className="mt-6 rounded-lg border border-gold/40 bg-gold/5 p-5">
              <p className="text-sm text-slate-200">
                Accept or decline this job in the Chauffeur app.
              </p>
              <Button asChild className="mt-4 h-12 w-full px-3 text-sm bg-gold text-ink-900 hover:bg-gold-light">
                <a data-testid="driver-open-app-btn" href="/portal/driver">
                  <Smartphone className="mr-2 h-4 w-4 shrink-0" /> Open the Chauffeur app
                </a>
              </Button>
              <p className="mt-3 font-mono text-[11px] text-slate-500">
                Sign in with the email this manifest was sent to.
              </p>
            </div>
          )}

          <p className="mt-6 text-[11px] leading-relaxed text-slate-500">
            <strong className="text-slate-400">Transportation Service Disclaimer</strong> — This transportation
            service is provided by {c.name}, the transportation operator identified on this reservation. The
            software platform facilitates communication, scheduling, dispatch and payment technology but does not
            provide or operate the transportation service.
          </p>
          {c.show_powered_by && (
            <p data-testid="driver-powered-by" className="mt-4 font-mono text-[11px] text-slate-600">Powered by {PLATFORM_NAME}</p>
          )}
        </div>
      </div>
    </div>
  );
}
